import { Classes, Dialog } from "@blueprintjs/core";
import { observer } from "mobx-react";
import func from "modules/agents/functionalities";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Search } from "modules/common/components/form/Search";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { Block } from "modules/common/components/page/Block";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { isNewlyCreated } from "modules/common/models/entity";
import { routes } from "modules/common/routes";
import { texts } from "modules/common/texts";
import { RoleDetails } from "modules/session/access/components/roles/RoleDetails";
import { can, canAny } from "modules/session/auth/access";
import sessionFunc from "modules/session/functionalities";
import React from "react";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { OutsourcersStoreType } from "../models/outsourcers-store";
import styles from "./OutsourcersPage.module.scss";
import { OutsourcerDetails } from "./details/OutsourcerDetails";
import { OutsourcersList } from "./list/OutsourcersList";
import { AgentMailbox } from "modules/agents/agent-mail/components/AgentMailbox";
import OutsourcerRequests from "./requests/OutsourcerRequests";
import { withNavigation, NavigationProps, type Location } from "modules/common/components/routing/withNavigation";

// const localTabName = "AgentsFilterTab";
class OutsourcersPage extends React.Component<OutsourcersPageProps, OutsourcersPageState> {
  constructor(props: OutsourcersPageProps) {
    super(props);

    this.state = {
      outsourcerId: getSelectedOutsourcerId(props.location.pathname),
      showAccess: false,
      load: false,
      loadExport: false,
      showRequestsAgents: false,
      showOrderEmail: false,
      requestId: "",
    };
  }

  async componentDidMount() {
    const { store, access } = this.props;
    document.title = "Контрагенты";
    const canOutsourcerRead = canAny([func.OUTSOURCER_EDIT, func.OUTSOURCER_READ], access);

    const canClientRead = canAny([func.CLIENT_EDIT, func.CLIENT_READ], access);

    const canSupplierRead = canAny([func.SUPPLIER_EDIT, func.SUPPLIER_READ], access);

    const canAgentRead = canAny([func.AGENT_EDIT, func.AGENT_READ], access);
    const canReadAll = canOutsourcerRead && canClientRead && canSupplierRead && canAgentRead;
    store.init(this.state.outsourcerId, this.props.access);

    if (canAgentRead) {
      store.list.tabSelect("agent");
    }
    if (canSupplierRead) {
      store.list.tabSelect("supplier");
    }
    if (canClientRead) {
      store.list.tabSelect("client");
    }
    if (canOutsourcerRead) {
      store.list.tabSelect("outsourcer");
    }
    if (canReadAll) {
      store.list.tabSelect("");
    }
    // ссылка с главной («Запросы» → заявка агента) сразу открывает нужную заявку
    const requestId = new URLSearchParams(this.props.location.search).get("request");
    if (requestId && canAgentRead) {
      this.setState({ showRequestsAgents: true, requestId });
    }
  }

  componentDidUpdate(prevProps: NavigationProps) {
    // смена адреса приходит новым location в пропах
    if (prevProps.location !== this.props.location) {
      this.onLocationChanged(this.props.location);
    }
  }

  onLocationChanged = async (location: Location) => {
    const outsourcerId = getSelectedOutsourcerId(location.pathname);
    if (this.state.outsourcerId !== outsourcerId) {
      this.setState({ outsourcerId, load: true });
      await this.loadDetails(outsourcerId);
      this.setState({ load: false });
    }
  };

  title = (header: string) => {
    const showButtons = !isNewlyCreated(this.state.outsourcerId);
    const canAccess = showButtons && can(sessionFunc.ACCESS_EDIT, this.props.access);

    return (
      <div className={styles.title}>
        {header}
        {canAccess && (
          <PlanrButton type="secondary" size="small" onClick={this.showAccess} className={styles.showAccess}>
            Права доступа
          </PlanrButton>
        )}
      </div>
    );
  };

  render() {
    const { store, access, baseUrl } = this.props;
    const { loading } = store;
    const { showAccess, showRequestsAgents, showOrderEmail } = this.state;
    const { tabIndex, outsourceIndex } = store.list;

    const canChange = canAny([func.OUTSOURCER_EDIT, func.AGENT_EDIT, func.CLIENT_EDIT, func.SUPPLIER_EDIT], access);
    const canConfirm = can(func.OUTSOURCER_CONFIRMATION, access);
    const canSupplierOrder = can(func.AGENTS_SUPPLIER_ORDER, access);
    const canOutsourcerRead = canAny([func.OUTSOURCER_EDIT, func.OUTSOURCER_READ], access);
    const canOutsourcerEdit = can(func.OUTSOURCER_EDIT, access);

    const canClientRead = canAny([func.CLIENT_EDIT, func.CLIENT_READ], access);
    const canClientEdit = can(func.CLIENT_EDIT, access);

    const canSupplierRead = canAny([func.SUPPLIER_EDIT, func.SUPPLIER_READ], access);
    const canSupplierEdit = can(func.SUPPLIER_EDIT, access);

    const canAgentRead = canAny([func.AGENT_EDIT, func.AGENT_READ], access);
    const canAgentEdit = can(func.AGENT_EDIT, access);
    const canAgentConfirm = can(func.AGENT_CONFIRMATION, access);
    const canAgentRequest = can(func.AGENT_REQUEST, access);
    const canReadAll = canOutsourcerRead && canClientRead && canSupplierRead && canAgentRead;

    const path = routes.agents.outsourcers.details.outsourcers();
    const title = "Контрагент";
    const { outsourcers } = store.requestsList;
    return (
      <Block className={styles.page}>
        {canChange && (
          <PlanrButton
            type="secondary"
            icon="general-plus-big"
            onClick={this.onOutsourcerAdd}
            title={texts.add}
            className={styles.addButton}
            size="small"
          >
            Добавить
          </PlanrButton>
        )}
        {canAgentRequest && (
          <PlanrButton
            type="lightblueish"
            icon="general-doc-check"
            onClick={this.toggleShowRequestEmail}
            title={texts.add}
            className={styles.orderRequestButton}
            size="small"
          >
            Запросы
          </PlanrButton>
        )}
        {canAgentConfirm && (
          <div>
            <PlanrButton
              type="lightgreenish"
              icon="general-add-user"
              onClick={this.toggleShowRequests}
              title={"Заявки"}
              className={styles.requestsButton}
              size="small"
            >
              Заявки
            </PlanrButton>
            {outsourcers && outsourcers.length > 0 && (
              <div className={styles.requestCount}>{outsourcers.length >= 100 ? "99+" : outsourcers.length}</div>
            )}
          </div>
        )}
        {showRequestsAgents && (
          <Dialog
            isOpen={showRequestsAgents}
            title={"Заявки"}
            backdropClassName="standard"
            onClose={this.toggleShowRequests}
            className={`${styles.FilesDialog} figma-dialog`}
          >
            <OutsourcerRequests {...this.props} initialRequestId={this.state.requestId} />
          </Dialog>
        )}
        {showOrderEmail && (
          <DetailsPopup title={"Переписка с агентами"} onCancel={this.toggleShowRequestEmail} width={1454}>
            <AgentMailbox
              agents={store.list.data}
              categories={store.categories}
              orderDictionary={store.orderDictionary}
              baseUrl={baseUrl}
              store={store.agentMail}
            />
          </DetailsPopup>
        )}
        <div className={styles.tabButtons}>
          {canReadAll && (
            <PlanrButton
              style={{ width: "120px" }}
              type={tabIndex === "" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                // localStorage.setItem(localTabName, JSON.stringify(""));

                store.list.tabSelect("");
                store.list.tabOutsourceSelect("");
              }}
            >
              Все
            </PlanrButton>
          )}
          {canOutsourcerRead && (
            <PlanrButton
              style={{ width: "120px" }}
              type={tabIndex === "outsourcer" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                // localStorage.setItem(localTabName, JSON.stringify("outsourcer"));

                store.list.tabSelect("outsourcer");
              }}
            >
              Аутсорсеры
            </PlanrButton>
          )}
          {canClientRead && (
            <PlanrButton
              style={{ width: "120px" }}
              type={tabIndex === "client" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                // localStorage.setItem(localTabName, JSON.stringify("client"));

                store.list.tabSelect("client");
                store.list.tabOutsourceSelect("");
              }}
            >
              Заказчики
            </PlanrButton>
          )}
          {canSupplierRead && (
            <PlanrButton
              style={{ width: "120px" }}
              type={tabIndex === "supplier" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                // localStorage.setItem(localTabName, JSON.stringify("supplier"));
                store.list.tabSelect("supplier");
                store.list.tabOutsourceSelect("");
              }}
            >
              Поставщики
            </PlanrButton>
          )}
          {canAgentRead && (
            <PlanrButton
              style={{ width: "120px" }}
              type={tabIndex === "agent" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                // localStorage.setItem(localTabName, JSON.stringify("agent"));
                store.list.tabSelect("agent");
                store.list.tabOutsourceSelect("");
              }}
            >
              Агенты
            </PlanrButton>
          )}
        </div>
        {tabIndex === "outsourcer" && (
          <div className={styles.tabButtons2}>
            <PlanrButton
              style={{ width: "120px" }}
              type={outsourceIndex === "" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                store.list.tabOutsourceSelect("");
              }}
            >
              Все
            </PlanrButton>

            <PlanrButton
              style={{ width: "120px" }}
              type={outsourceIndex === "legalOutsourcer" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                store.list.tabOutsourceSelect("legalOutsourcer");
              }}
            >
              Юр.лица
            </PlanrButton>
            <PlanrButton
              style={{ width: "120px" }}
              type={outsourceIndex === "physOutsourcer" ? "blueish" : "lightblueish"}
              size="small"
              onClick={() => {
                store.list.tabOutsourceSelect("physOutsourcer");
              }}
            >
              Физ.лица
            </PlanrButton>
          </div>
        )}
        <div className={styles.actions}>
          <div className={styles.right}>
            <PlanrButton
              type="graybtn"
              icon="general-export"
              title={texts.export}
              onClick={async () => {
                try {
                  this.setState({ loadExport: true });
                  await store.export();
                } catch (e) {
                } finally {
                  this.setState({ loadExport: false });
                }
              }}
              round={true}
            />
            <PlanrButton
              type="graybtn"
              icon="general-print"
              onClick={async () => {
                try {
                  this.setState({ loadExport: true });
                  await store.print();
                } catch (e) {
                } finally {
                  this.setState({ loadExport: false });
                }
              }}
              title={texts.export}
              round={true}
            />
            <Search query={store.list.query} onSearch={this.props.store.list.setQuery} />
          </div>
        </div>
        {!this.state.load && (
          <WhenPath path={path}>
            <DetailsPopup title={this.title(title)} onCancel={this.onOutsourcerSelected} width={1754}>
              <OutsourcerDetails
                baseUrl={baseUrl}
                access={access}
                canChange={canChange}
                canRemove={canChange}
                canConfirm={canConfirm}
                canSupplierOrder={canSupplierOrder}
                canOutsourcerEdit={canOutsourcerEdit}
                canSupplierEdit={canSupplierEdit}
                canClientEdit={canClientEdit}
                canAgentEdit={canAgentEdit}
                workTypes={store.workTypes}
                outsourcer={store.details}
                onSaved={this.onOutsourcerSaved}
                onAccessChanged={this.loadList}
                onRemoved={this.onOutsourcerRemoved}
                onPrintCredentials={store.printCredentials}
                onPrintDocCredentials={this.props.store.printDocCredentials}
                confirmRequest={this.props.store.confirmRequest}
                showSensitiveData={true}
                categories={store.categories}
              />
            </DetailsPopup>

            <Dialog
              icon="info-sign"
              title="Права доступа"
              canEscapeKeyClose={true}
              canOutsideClickClose={false}
              isOpen={showAccess}
              onClose={this.hideAccess}
              style={{ width: `1180px` }}
              backdropClassName="standard"
            >
              <div className={`${Classes.DIALOG_BODY} ${styles.userAccess}`}>
                <RoleDetails role={store.details.role} columns={store.accessColumns} onToggle={this.setFunctionality} />
              </div>
            </Dialog>
          </WhenPath>
        )}

        {loading || this.state.loadExport ? (
          <LocalLoader active zIndex={0} />
        ) : (
          <OutsourcersList
            store={store.list}
            onOutsourcerSelect={this.onOutsourcerSelected}
            onOutsourcerRemoved={this.onOutsourcerRemoved}
          />
        )}
      </Block>
    );
  }

  onOutsourcerSelected = (outsourcerId: string = "") => {
    const { navigate } = this.props;
    const listUrl = routes.agents.outsourcers.outsourcers;

    const detailsUrl = routes.agents.outsourcers.details.outsourcers(outsourcerId);

    navigate(outsourcerId ? detailsUrl : listUrl);
  };

  onOutsourcerSaved = () => {
    this.onOutsourcerSelected("");
    this.loadList();
  };

  onOutsourcerRemoved = () => {
    const { navigate } = this.props;

    this.loadList();

    const url = routes.agents.outsourcers.outsourcers;
    navigate(url);
  };

  onOutsourcerAdd = () => {
    this.onOutsourcerSelected(EMPTY_OBJECT_ID);
  };

  loadList = () => this.props.store.load();

  loadDetails = async (id: string) => {
    const { store } = this.props;

    // prefill form with cached values before loading
    // const outsourcer = store.list.asMap[id];

    // if (outsourcer) {
    //   applySnapshot(store.details, getSnapshot(outsourcer));
    // }

    await store.details.load(id);
  };

  showAccess = () => this.setState({ showAccess: true });
  hideAccess = () => this.setState({ showAccess: false });

  toggleShowRequests = () => this.setState({ showRequestsAgents: !this.state.showRequestsAgents, requestId: "" });
  toggleShowRequestEmail = () => this.setState({ showOrderEmail: !this.state.showOrderEmail });

  setFunctionality = (roleId: string, functionality: string, state: boolean) =>
    this.props.store.details.setFunctionality(functionality, state);
}

export default withNavigation(observer(OutsourcersPage));

function getSelectedOutsourcerId(pathname: string) {
  const detailsUrl = routes.agents.outsourcers.details.outsourcers();

  const match = matchRoute<OutsourcersId>(pathname, detailsUrl);

  return match && match.params ? match.params.outsourcerId : "";
}

interface OutsourcersPageProps extends NavigationProps {
  store: OutsourcersStoreType;
  isLegalEntities: boolean;
  access: string[];
  baseUrl: string;
}

interface OutsourcersId {
  outsourcerId: string;
}

interface OutsourcersPageState extends OutsourcersId {
  showAccess: boolean;
  showRequestsAgents: boolean;
  requestId: string;
  showOrderEmail: boolean;
  load: boolean;
  loadExport: boolean;
}
